import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { grantsForScope } from "../rbac/grantQueries";
import type { BuiltinRoleId } from "../rbac/roles";
import { assertDependentPrincipalsMeetRequirements } from "../rbac/validation";
import { assertRoleDoesNotGrant } from "../rbac/validation/grantContent";
import { mutator } from "./mutator";
import { generateId } from "./utils";

export const joinSpaceSchema = z.object({
  spaceId: z.string(),
});

export const joinSpace = mutator({
  schema: joinSpaceSchema,
  permissions: {
    none: "self-service joining, gated in fn by the space’s default join role",
  },
  resolve: {},
  fn: async ({ tx, ctx, args: { spaceId } }) => {
    const space = await tx.run(zql.spaces.where("id", spaceId).one());
    if (!space) throw userFacingError("Space not found");

    if (space.mode === "personal") {
      throw userFacingError("This space cannot be joined");
    }

    const builtinRoleId = space.defaultJoinBuiltinRoleId;
    const customRoleId = space.defaultJoinCustomRoleId;
    if (!builtinRoleId && !customRoleId) {
      throw userFacingError("This space is not discoverable");
    }

    const [user] = await tx.run(zql.user.where("id", ctx.userId));
    if (!user) throw userFacingError("User not found");
    if (user.tenantId !== space.tenantId) {
      throw userFacingError("Space not found");
    }

    await assertRoleDoesNotGrant(
      tx,
      { builtinRoleId, customRoleId },
      "space:manage"
    );

    const existingGrant = await tx.run(
      grantsForScope(space.id, ctx.tenantId).where("userId", ctx.userId)
    );
    if (existingGrant.length > 0) return;

    const customRole = customRoleId
      ? (
          await tx.run(
            zql.roles.where("id", customRoleId).related("rolePermissions")
          )
        )[0]
      : null;

    const principalGrant = {
      userId: ctx.userId,
      userGroupId: null,
      builtinRoleId: builtinRoleId ?? null,
      customRole: customRole
        ? { rolePermissions: customRole.rolePermissions ?? [] }
        : null,
    };
    for (const ruleId of [
      "space_uses_connected_apps",
      "space_uses_skills",
    ] as const) {
      await assertDependentPrincipalsMeetRequirements(
        tx,
        ctx.tenantId,
        ruleId,
        space.id,
        [principalGrant]
      );
    }

    await tx.mutate.roleGrants.insert({
      id: generateId(),
      userId: ctx.userId,
      builtinRoleId: (builtinRoleId as BuiltinRoleId | null) ?? undefined,
      customRoleId: customRoleId ?? undefined,
      scopeId: space.id,
      createdAt: Date.now(),
      tenantId: ctx.tenantId,
    });
  },
});
